import React, { useState } from "react";
import {
  Button,
  Input,
  Label,
  Dropdown,
  Option,
  Switch,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import { AddRegular, DeleteRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import SearchResourceInGraph from "../../GenericComponent/SearchResourceInGraph";
import GenerateAudioContainer from "../../../Containers/GenericContainer/GenerateAudioContainer";
import "./CSS/IvrComponent.css";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "nowrap",
  },
  keyRow: {
    display: "grid",
    gridTemplateColumns: "60px 220px 2fr 40px",
    alignItems: "center",
    columnGap: "1rem",
    padding: "0.5rem",
    paddingLeft: "1rem",
    backgroundColor: "#fff",
    borderRadius: "4px",
    border: "none",
    marginBottom: "0.35rem",
  },
  dialTableHeader: {
    display: "grid",
    gridTemplateColumns: "60px 220px 2fr 40px",
    columnGap: "1rem",
    fontWeight: 600,
    padding: "0.35rem 0.5rem",
    paddingLeft: "1rem",
    marginTop: "0.5rem",
    marginBottom: "0.35rem",
    backgroundColor: "#f3f3f3",
    borderRadius: "4px",
    fontSize: "13px",
  },
  dialTableContainer: {
    backgroundColor: "#fff",
    borderRadius: "6px",
    padding: "0.75rem",
    border: "none",
    marginTop: "0.5rem",
    marginBottom: "2rem",
    position: "relative",
    overflow: "visible",
  },
  addKeyRow: {
    display: "grid",
    gridTemplateColumns: "60px 140px 2fr 40px",
    alignItems: "center",
    columnGap: "1rem",
    padding: "0.5rem",
    paddingLeft: "1rem",
    backgroundColor: "#f8f8f8",
    borderRadius: "4px",
    border: "none",
    marginTop: "0.35rem",
    position: "relative",
    minWidth: "fit-content",
  },
});

const ACTION_TYPES = [
  { key: "queue", textKey: "queue" },
  { key: "transferUser", textKey: "transferToUser" },
  { key: "disconnect", textKey: "disconnect" },
  { key: "routing", textKey: "routing" },
  { key: "transferPhone", textKey: "transferToPhone" },
];

export default function IvrComponent({
  data,
  routingName,
  onChange,
  onAddOption,
  onRemoveOption,
  ACTIVE_TENANT,
  allRoutings = {},
  callFlowData,
  permissions,
  isEditMode
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [newKey, setNewKey] = useState("");
  const [newActionType, setNewActionType] = useState("");
  const [newTarget, setNewTarget] = useState(null);

  const handleToggleHash = (_, val) => {
    const lastTone = val.checked ? "#" : "";
    onChange(routingName, "lastTone", lastTone);
  };

  const buildOptionFromSelection = (actionType, target) => {
    switch (actionType) {
      case "queue":
        return { type: "queue", queueId: target.id, queueDisplayName: target.displayName };
      case "transferUser":
        return { type: "transfer", targetId: target.id, targetType: "user", userDisplayName: target.displayName };
      case "disconnect":
        return { type: "disconnect" };
      case "routing":
        return { type: "routing", routingName: target.routingName };
      case "transferPhone":
        return { type: "transfer", targetId: target.phoneNumber, targetType: "phone" };
      default:
        return null;
    }
  };

  const handleAddKey = () => {
    if (!newKey || !newActionType) return;
    let target = newTarget;
    if (newActionType === "disconnect") target = {};
    if (!target && newActionType !== "disconnect") return;

    const option = buildOptionFromSelection(newActionType, target);
    if (!option) return;

    onAddOption(newKey, option);
    setNewKey("");
    setNewActionType("");
    setNewTarget(null);
  };

  const audio = data?.audio || {};
  const availableRoutings = Object.values(allRoutings).filter((r) => r.routingName !== routingName);

  // Normaliser le type audio
  let normalizedAudioType = audio.type || "Text2speech";
  if (normalizedAudioType.toLowerCase() === "text2speech") {
    normalizedAudioType = "Text2speech";
  } else if (normalizedAudioType.toLowerCase() === "audiofile") {
    normalizedAudioType = "Audiofile";
  }
  
  // Si audioFileUrl existe, le type devrait être Audiofile
  if (audio.audioFileUrl && !audio.text) {
    normalizedAudioType = "Audiofile";
  }

  return (
    <div className={classes.container}>
      {/* IVR name */}
      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>{t("ivrName")}</Label>
        <Input
          style={{ width: '100%', backgroundColor: '#f3f2f1', border: 'none', borderRadius: '4px' }}
          value={data?.displayName || ""}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onChange={(_, v) => onChange(routingName, "displayName", v.value)}
        />
      </div>

      {/* Wait until # */}
      <div className={classes.row}>
        <Label style={{ fontWeight: 600, whiteSpace: 'nowrap', flex: '0 0 auto' }}>{t("waitUntilCustomerEnters")}</Label>
        <Switch 
          checked={data?.lastTone === "#"} 
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onChange={handleToggleHash} 
        />
      </div>

      <div className={classes.row}>
        <Label style={{ fontWeight: 600, minWidth: '200px' }}>{t("expectedDigitsNumber")}</Label>
        <Input
          className="ivr-input-small"
          type="number"
          value={data?.expectedDigitNumber ?? 1}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onChange={(_, v) => onChange(routingName, "expectedDigitNumber", Number(v.value || 1))}
        />
      </div>

      {/* Timeout (secondes) avant de jouer / reprendre — défaut 10s */}
      <div className={classes.row}>
        <Label style={{ fontWeight: 600, minWidth: '200px' }}>
          {t("timeoutSeconds", { defaultValue: "Timeout (seconds)" })}
        </Label>
        <Input
          className="ivr-input-small"
          type="number"
          min={1}
          value={data?.timeout ?? 10}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onChange={(_, v) => onChange(routingName, "timeout", Number(v.value || 10))}
        />
      </div>

      {/* Audio */}
      <Label style={{ fontWeight: 600 }}>{t("message")}</Label>
      <div style={{ marginLeft: '2rem' }}>
        <GenerateAudioContainer
        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
        canPlayAudio={permissions?.routing?.canRead}
        setLangue={(lang) => onChange(routingName, "audio.language", lang)}
        setDelete={() => {
          onChange(routingName, "audio.audioFileUrl", "");
          onChange(routingName, "audio.text", "");
          onChange(routingName, "audio.type", "Text2speech");
        }}
        saveAudio={(url) => onChange(routingName, "audio.audioFileUrl", url)}
        setText={(text) => {
          onChange(routingName, "audio.text", text);
          onChange(routingName, "audio.audioFileUrl", "");
          onChange(routingName, "audio.type", "Text2speech");
        }}
        setAudio={(selectedMedia) => {
          onChange(routingName, "audio.selectedMedia", selectedMedia);
          onChange(routingName, "audio.audioFileUrl", selectedMedia?.url || "");
          onChange(routingName, "audio.text", "");
          onChange(routingName, "audio.type", "Audiofile");
        }}
        setType={(type) => onChange(routingName, "audio.type", type)}
        audioFileUrl={audio.audioFileUrl}
        fileName={audio?.selectedMedia?.header}
        type={normalizedAudioType}
        text={audio.text || ""}
        path={`${ACTIVE_TENANT}/${callFlowData.id}/${routingName}`}
        defaultLanguage={audio.language || callFlowData?.language || "en-US"}
      />
      </div>

      {/* Dial keys */}
      <div className={classes.dialTableContainer}>
        <Label weight="semibold" className="ivr-table-label">
          {t("ivr")}
        </Label>
        
        <div className={classes.dialTableHeader}>
          <span>{t("key")}</span>
          <span>{t("action")}</span>
          <span>{t("redirectTo")}</span>
          <span></span>
        </div>

        {/* Existing keys */}
        {data?.options &&
          Object.keys(data.options).map((k) => {
            const opt = data.options[k];
            let actionLabel = t("selectAction");
            let redirectLabel = "";

            if (opt.type === "queue") {
              actionLabel = t("queue");
              redirectLabel = opt.queueDisplayName || opt.queueId;
            } else if (opt.type === "transferUser" || (opt.type === "transfer" && opt.targetType === "user")) {
              actionLabel = t("transferToUser");
              redirectLabel = opt.userDisplayName || opt.targetId;
            } else if (opt.type === "transferPhone" || (opt.type === "transfer" && opt.targetType === "phone")) {
              actionLabel = t("transferToPhone");
              redirectLabel = opt.targetId;
            } else if (opt.type === "disconnect") {
              actionLabel = t("disconnect");
              redirectLabel = "—";
            } else if (opt.type === "routing") {
              actionLabel = t("routing");
              redirectLabel = opt.routingName;
            }

            return (
              <div key={k} className={classes.keyRow}>
                <Text weight="semibold">{k}</Text>
                <Text>{actionLabel}</Text>
                <Text className="ivr-redirect-text">
                  {redirectLabel}
                </Text>
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => onRemoveOption(k)}
                  size="small"
                />
              </div>
            );
          })}

        {/* Add new key */}
        <div className={classes.addKeyRow}>
          <div style={{ overflow: 'hidden' }}>
            <input
              placeholder={t("keyPlaceholder")}
              value={newKey}
              disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
              onChange={(e) => setNewKey(e.target.value)}
              style={{
                width: '100%',
                height: '32px',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                paddingLeft: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ overflow: 'hidden' }}>
            <select
              value={newActionType}
              disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
              onChange={(e) => setNewActionType(e.target.value)}
              style={{
                width: '100%',
                height: '32px',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                paddingLeft: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">{t("selectAction")}</option>
              {ACTION_TYPES.map((a) => (
                <option key={a.key} value={a.key}>
                  {t(a.textKey)}
                </option>
              ))}
            </select>
          </div>

          <div>
            {newActionType === "queue" && (
              <QueueSelectorComponent 
                displayName={newTarget?.displayName || ""} 
                disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
                getData={setNewTarget} 
              />
            )}
            {newActionType === "transferUser" && (
              <SearchResourceInGraph displayName={newTarget?.displayName || ""} onSelectUser={setNewTarget} />
            )}
            {newActionType === "routing" && (
              <Dropdown
                placeholder={t("selectRouting")}
                value={newTarget?.routingName || ""}
                onOptionSelect={(_, d) => setNewTarget({ routingName: d.optionValue })}
                size="small"
              >
                {availableRoutings.map((r) => (
                  <Option key={r.routingName} value={r.routingName}>
                    {r.displayName || r.routingName}
                  </Option>
                ))}
              </Dropdown>
            )}
            {newActionType === "transferPhone" && (
              <input
                placeholder={t("phoneNumber")}
                value={newTarget?.phoneNumber || ""}
                onChange={(e) => setNewTarget({ phoneNumber: e.target.value })}
                style={{
                  width: '100%',
                  height: '32px',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  paddingLeft: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            )}
            {newActionType === "disconnect" && <Text className="ivr-disconnect-text">{t("callWillBeDisconnected")}</Text>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              appearance="primary"
              icon={<AddRegular />}
              disabled={
                !newKey || 
                !newActionType || 
                (newActionType === "queue" && !newTarget?.id) ||
                (newActionType === "transferUser" && !newTarget?.displayName) ||
                (newActionType === "transferPhone" && !newTarget?.phoneNumber) ||
                (newActionType === "routing" && !newTarget?.routingName)
              }
              onClick={handleAddKey}
              size="small"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
